import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { adminGet, adminDelete, adminPost, adminPut, adminUploadImage } from "../../api/adminClient";
import Icon from "../../components/AppIcon";
import { useToast } from "../../hooks/use-toast";
import { API_BASE_URL } from "../../api/client";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { InlineLoader } from "../../components/ui/Loader";

function slugify(s) {
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

const defaultForm = {
  product_type_id: "",
  name: "",
  slug: "",
  description: "",
  short_description: "",
  manufacturer: "",
  model: "",
  sku: "",
  main_image: "",
  price: "",
  original_price: "",
  availability: "In Stock",
  is_featured: false,
  is_new: false,
};

const ProductsList = () => {
  const { success, error: showError } = useToast();
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterProductTypeId, setFilterProductTypeId] = useState("");
  const [filterAvailability, setFilterAvailability] = useState("");
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [prods, types] = await Promise.all([
        adminGet(
          `/admin/products?${new URLSearchParams({
            page: String(page),
            per_page: String(perPage),
            ...(search && search.trim() ? { search: search.trim() } : {}),
            ...(filterProductTypeId ? { product_type_id: String(filterProductTypeId) } : {}),
            ...(filterAvailability ? { availability: filterAvailability } : {}),
          }).toString()}`
        ),
        adminGet("/admin/products/product-types?page=1&per_page=100&include_inactive=false"),
      ]);
      const allItems = Array.isArray(prods) ? prods : (Array.isArray(prods?.items) ? prods.items : []);
      const total = typeof prods?.total === "number" ? prods.total : allItems.length;
      const totalPages = typeof prods?.total_pages === "number" ? prods.total_pages : Math.max(1, Math.ceil(total / perPage));
      const start = (page - 1) * perPage;
      const items = total === allItems.length ? allItems.slice(start, start + perPage) : allItems;
      setProducts(items);
      if (prods?.stats) {
        setStats(prods.stats);
      } else {
        setStats({
          total: allItems.length,
          in_stock: allItems.filter((p) => (p.availability || "In Stock") === "In Stock").length,
          pre_order: allItems.filter((p) => p.availability === "Pre-Order").length,
          coming_soon: allItems.filter((p) => p.availability === "Coming Soon").length,
          out_of_stock: allItems.filter((p) => p.availability === "Out of Stock").length,
        });
      }
      setTotalCount(total);
      setTotalPages(totalPages);

      const typeItems = Array.isArray(types) ? types : (Array.isArray(types?.items) ? types.items : []);
      setProductTypes(typeItems);
    } catch (e) {
      if (e?.status !== 401 && !e?.isSessionExpired) setError(e?.message || "Failed to load products");
      if (e?.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await load();
      if (initialLoading) {
        setInitialLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, filterProductTypeId, filterAvailability]);

  // Debounce search; only trigger API when >= 3 chars or cleared
  useEffect(() => {
    const trimmed = searchInput.trim();
    const handler = setTimeout(() => {
      if (trimmed === "") {
        setPage(1);
        setSearch("");
      } else if (trimmed.length >= 3) {
        setPage(1);
        setSearch(trimmed);
      }
      // 1–2 chars: keep previous search term
    }, 350);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const validateForm = () => {
    const err = {};
    if (!form.product_type_id || form.product_type_id === "") {
      err.product_type_id = "Category is required";
    }
    if (!form.name?.trim()) {
      err.name = "Name is required";
    } else if (form.name.trim().length < 2) {
      err.name = "Name must be at least 2 characters";
    }
    if (!form.slug?.trim()) {
      err.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(form.slug.trim())) {
      err.slug = "Slug can only contain lowercase letters, numbers, and hyphens";
    }
    if (form.price && form.price !== "" && (isNaN(parseFloat(form.price)) || parseFloat(form.price) < 0)) {
      err.price = "Price must be 0 or greater";
    }
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleFieldChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "name" && !editingProduct) {
      setForm((f) => ({ ...f, slug: slugify(value) }));
    }
    if (formErrors[key]) {
      setFormErrors((e) => {
        const next = { ...e };
        delete next[key];
        return next;
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setFormErrors((e) => ({ ...e, main_image: "Please select a valid image file" }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors((e) => ({ ...e, main_image: "Image must be less than 5MB" }));
        return;
      }
      setFormErrors((e) => {
        const next = { ...e };
        delete next.main_image;
        return next;
      });
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setForm(defaultForm);
    setFormErrors({});
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = async (p) => {
    setEditingProduct(p);

    // Fetch full product details so we have product_type_id and all fields
    let detail = null;
    try {
      detail = await adminGet(`/admin/products/${p.id}`);
    } catch (e) {
      // If detail fetch fails, fall back to list item data
      // but dropdown may not be fully accurate without product_type_id
    }

    const src = detail || p || {};
    const imgPath = src.main_image || src.image;

    setForm({
      product_type_id: src.product_type_id ?? p.product_type_id ?? "",
      name: src.name ?? "",
      slug: src.slug ?? "",
      description: src.description ?? "",
      short_description: src.short_description ?? "",
      manufacturer: src.manufacturer ?? "",
      model: src.model ?? "",
      sku: src.sku ?? "",
      main_image: src.main_image ?? src.image ?? "",
      price: src.price != null ? String(src.price) : "",
      original_price: src.original_price != null ? String(src.original_price) : "",
      availability: src.availability ?? "In Stock",
      is_featured: src.is_featured ?? false,
      is_new: src.is_new ?? false,
    });

    setFormErrors({});
    setImageFile(null);
    setImagePreview(
      imgPath
        ? imgPath.startsWith("http")
          ? imgPath
          : `${API_BASE_URL.replace("/api/v1", "")}${imgPath.startsWith("/") ? "" : "/"}${imgPath}`
        : null
    );
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setForm(defaultForm);
    setFormErrors({});
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowModal(false);
    setLoading(true);
    try {
      let mainImage = form.main_image?.trim() || null;
      
      const payload = {
        product_type_id: Number(form.product_type_id),
        name: form.name.trim(),
        slug: form.slug.trim().toLowerCase().replace(/\s+/g, "-"),
        description: form.description?.trim() || null,
        short_description: form.short_description?.trim() || null,
        manufacturer: form.manufacturer?.trim() || null,
        model: form.model?.trim() || null,
        sku: form.sku?.trim() || null,
        main_image: mainImage,
        price: form.price ? Number(form.price) : null,
        original_price: form.original_price ? Number(form.original_price) : null,
        availability: form.availability || "In Stock",
        is_featured: Boolean(form.is_featured),
        is_new: Boolean(form.is_new),
      };

      if (editingProduct) {
        // Edit mode: Upload image first (we have the ID)
        if (imageFile) {
          const fd = new FormData();
          fd.append("file", imageFile);
          fd.append("product_id", editingProduct.id);
          const res = await adminUploadImage(fd);
          payload.main_image = res?.image_url || res?.url || mainImage;
        }
        await adminPut(`/admin/products/${editingProduct.id}`, payload);
        success("Product updated successfully");
      } else {
        // Create mode: Create product first to get ID
        const createRes = await adminPost("/admin/products", payload);
        const newProductId = createRes?.data?.id || createRes?.id;
        
        if (imageFile && newProductId) {
          // Now upload image with the new ID
          const fd = new FormData();
          fd.append("file", imageFile);
          fd.append("product_id", newProductId);
          const uploadRes = await adminUploadImage(fd);
          const finalImageUrl = uploadRes?.image_url || uploadRes?.url;
          
          if (finalImageUrl) {
            // Update the product with the image URL
            await adminPut(`/admin/products/${newProductId}`, {
              ...payload,
              main_image: finalImageUrl
            });
          }
        }
        success("Product created successfully");
      }
      
      closeModal();
      await load();
    } catch (err) {
      setFormErrors((e) => ({ ...e, _submit: err?.message || "Save failed" }));
      setShowModal(true);
      showError(err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (p) => {
    setProductToDelete(p);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    setDeleteModalOpen(false);
    setLoading(true);
    try {
      await adminDelete(`/admin/products/${productToDelete.id}`);
      setProductToDelete(null);
      success("Product deleted successfully");
      await load();
    } catch (e) {
      showError(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      form.product_type_id &&
      form.name?.trim() &&
      form.name.trim().length >= 2 &&
      form.slug?.trim() &&
      /^[a-z0-9-]+$/.test(form.slug.trim()) &&
      (form.price === "" || form.price === null || (!isNaN(parseFloat(form.price)) && parseFloat(form.price) >= 0))
    );
  };

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Products</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage your product catalog</p>
        </div>
        <div className="bg-card rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="flex flex-col items-center justify-center py-12">
            <InlineLoader text="Loading products…" size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Products</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage your product catalog</p>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 shadow-md hover:shadow-lg transition-shadow shrink-0"
        >
          <Icon name="Plus" size={20} />
          Add Product
        </button>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="bg-card rounded-xl shadow-lg border border-border p-4 sm:p-6 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">Total Products</p>
              <p className="mt-1 text-2xl font-bold text-text-primary">{stats?.total ?? totalCount}</p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
              <Icon name="Package" size={22} className="text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">In Stock</p>
              <p className="mt-1 text-2xl font-bold text-success">
                {stats?.in_stock ?? products.filter((p) => (p.availability || "In Stock") === "In Stock").length}
              </p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-green-500 flex items-center justify-center shrink-0">
              <Icon name="CheckCircle" size={22} className="text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">Coming Soon</p>
              <p className="mt-1 text-2xl font-bold text-warning">
                {stats?.coming_soon ?? products.filter((p) => p.availability === "Coming Soon").length}
              </p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-warning flex items-center justify-center shrink-0">
              <Icon name="Clock" size={22} className="text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">Pre‑Order</p>
              <p className="mt-1 text-2xl font-bold text-accent">
                {stats?.pre_order ?? products.filter((p) => p.availability === "Pre-Order").length}
              </p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
              <Icon name="Star" size={22} className="text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-text-secondary">Out of Stock</p>
              <p className="mt-1 text-2xl font-bold text-error">
                {stats?.out_of_stock ?? products.filter((p) => p.availability === "Out of Stock").length}
              </p>
            </div>
            <div className="w-11 h-11 rounded-lg bg-red-500 flex items-center justify-center shrink-0">
              <Icon name="AlertTriangle" size={22} className="text-white" />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            {/* Large search bar using most of the width */}
            <div className="relative w-full sm:max-w-xl">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                placeholder="Search by name, description, manufacturer…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div className="flex flex-1 gap-3">
              {/* Smaller category dropdown */}
              <div className="relative w-full max-w-[220px]">
                <select
                  value={filterProductTypeId}
                  onChange={(e) => {
                    setPage(1);
                    setFilterProductTypeId(e.target.value || "");
                  }}
                  className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">All categories</option>
                  {productTypes.map((pt) => (
                    <option key={pt.product_type_id} value={pt.product_type_id}>
                      {pt.name}
                    </option>
                  ))}
                </select>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
              </div>
              <div className="relative w-full max-w-[180px]">
                <select
                  value={filterAvailability}
                  onChange={(e) => {
                    setPage(1);
                    setFilterAvailability(e.target.value || "");
                  }}
                  className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">All availability</option>
                  <option value="In Stock">In Stock</option>
                  <option value="Coming Soon">Coming Soon</option>
                  <option value="Pre-Order">Pre-Order</option>
                  <option value="Out of Stock">Out of Stock</option>
                </select>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setFilterProductTypeId("");
              setFilterAvailability("");
              setPage(1);
            }}
            className="inline-flex items-center gap-2 self-start rounded-xl border border-border px-3 py-2 text-xs font-medium text-text-secondary hover:bg-muted transition-colors"
          >
            <Icon name="XCircle" size={14} />
            Clear filters
          </button>
        </div>
      </motion.div>
      {error && (
        <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-sm">
          {error}
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-card rounded-xl shadow-lg border border-border overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-sm font-medium text-text-primary">Name</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary">Category</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary">Price</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary">Availability</th>
                <th className="px-4 py-3 text-sm font-medium text-text-primary w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <InlineLoader text="Loading products…" size="medium" />
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-text-secondary">
                    No products yet. Add one to get started.
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 text-text-primary">{p.name}</td>
                    <td className="px-4 py-3 text-text-secondary">{p.category}</td>
                    <td className="px-4 py-3 text-text-secondary">
                      {p.price != null ? `RM ${Number(p.price)}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-text-secondary">{p.availability || "In Stock"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(p)}
                          title="Edit"
                          className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(p)}
                          title="Delete"
                          className="p-2 rounded-lg hover:bg-muted text-error transition-colors"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border px-4 py-3">
          <p className="text-xs text-text-secondary">
            Showing{" "}
            {products.length === 0
              ? 0
              : (page - 1) * perPage + 1}{" "}
            –{" "}
            {(page - 1) * perPage + products.length} of {totalCount} products
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1 text-sm text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
            >
              <Icon name="ChevronLeft" size={14} />
              Prev
            </button>
            <span className="text-xs text-text-secondary">
              Page {page} of {totalPages || 1}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => (totalPages ? Math.min(totalPages, p + 1) : p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1 text-sm text-text-secondary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted"
            >
              Next
              <Icon name="ChevronRight" size={14} />
            </button>
          </div>
        </div>
      </motion.div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[999]">
          <div
            className="bg-card rounded-xl shadow-lg border border-border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-text-primary">
                {editingProduct ? "Edit Product" : "Add New Product"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-muted text-text-secondary"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {formErrors._submit && (
              <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
                {formErrors._submit}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Category *</label>
                <div className="relative">
                  <select
                    value={form.product_type_id}
                    onChange={(e) => handleFieldChange("product_type_id", e.target.value)}
                    className={`w-full px-3 py-2.5 pr-10 rounded-lg border bg-card text-text-primary appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                      formErrors.product_type_id ? "border-error" : "border-border"
                    }`}
                  >
                    <option value="">Select category</option>
                    {productTypes.map((pt) => (
                      <option key={pt.product_type_id} value={pt.product_type_id}>
                        {pt.name}
                      </option>
                    ))}
                  </select>
                  <Icon
                    name="ChevronDown"
                    size={18}
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary"
                  />
                </div>
                {formErrors.product_type_id && (
                  <p className="text-xs text-error mt-1">{formErrors.product_type_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border bg-background text-text-primary ${
                    formErrors.name ? "border-error" : "border-border"
                  }`}
                />
                {formErrors.name && <p className="text-xs text-error mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Slug *</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => handleFieldChange("slug", e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border bg-background text-text-primary ${
                    formErrors.slug ? "border-error" : "border-border"
                  }`}
                  placeholder="e.g. product-name"
                />
                {formErrors.slug && <p className="text-xs text-error mt-1">{formErrors.slug}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleFieldChange("description", e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Manufacturer</label>
                  <input
                    type="text"
                    value={form.manufacturer}
                    onChange={(e) => handleFieldChange("manufacturer", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Model</label>
                  <input
                    type="text"
                    value={form.model}
                    onChange={(e) => handleFieldChange("model", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Product Image</label>
                <div className="space-y-3">
                  <label className="flex flex-col items-center justify-center w-full min-h-[140px] rounded-xl border-2 border-dashed border-border bg-muted/30 hover:bg-muted/50 hover:border-primary/40 cursor-pointer transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    {imagePreview ? (
                      <div className="relative w-full p-3">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full max-h-[200px] object-contain rounded-lg border border-border bg-card"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setImageFile(null);
                            setImagePreview(null);
                            handleFieldChange("main_image", "");
                          }}
                          className="absolute top-4 right-4 p-1.5 rounded-lg bg-error/90 text-white hover:bg-error transition-colors"
                          aria-label="Remove image"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-6 px-4 text-text-secondary">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon name="Upload" size={24} className="text-primary" />
                        </div>
                        <span className="text-sm font-medium text-text-primary">Click to upload image</span>
                        <span className="text-xs">PNG, JPG, GIF, WebP (max 5MB)</span>
                      </div>
                    )}
                  </label>
                  {formErrors.main_image && (
                    <p className="text-xs text-error">{formErrors.main_image}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Price (RM)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => handleFieldChange("price", e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border bg-background text-text-primary ${
                      formErrors.price ? "border-error" : "border-border"
                    }`}
                  />
                  {formErrors.price && <p className="text-xs text-error mt-1">{formErrors.price}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Availability</label>
                  <div className="relative">
                    <select
                      value={form.availability}
                      onChange={(e) => handleFieldChange("availability", e.target.value)}
                      className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border bg-card text-text-primary appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="In Stock">In Stock</option>
                      <option value="Coming Soon">Coming Soon</option>
                      <option value="Pre-Order">Pre-Order</option>
                      <option value="Out of Stock">Out of Stock</option>
                    </select>
                    <Icon
                      name="ChevronDown"
                      size={18}
                      className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-secondary"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={(e) => handleFieldChange("is_featured", e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-text-primary">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_new}
                    onChange={(e) => handleFieldChange("is_new", e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-sm text-text-primary">New</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-lg border border-border text-text-primary hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid()}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingProduct ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen && !!productToDelete}
        title="Delete Product"
        message={`Are you sure you want to soft delete "${productToDelete?.name}"? It will be hidden from the public list.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setProductToDelete(null); }}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default ProductsList;
